"use client";

import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { ContentfulInspectorManager } from "@packages/lib/contentful-preview/ContentfulInspector";

interface Props {
    propsdata: any;
    key?: string | number;
    preview?: boolean;
}

export default function ImbeddedVedio({ propsdata, key, preview }: Props) {
    const data = useContentfulLiveUpdates(propsdata);
    const [isVideoVisible, setIsVideoVisible] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const getYouTubeVideoId = (url: string) => {
        if (!url) return null;
        const match = url.match(
            /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/
        );
        return match ? match[1] : null;
    };
    const mediaType = data?.mediaType ? data?.mediaType?.toLowerCase() : "";
    const videoUrl = mediaType === "video" ? data?.mediaCollection?.items?.[0]?.url || "" : data?.externalVideoUrl;
    const thumbnailUrl = data?.thumbnail?.url || "";
    const [playVideo, setPlayVideo] = useState(false);
    const videoId = getYouTubeVideoId(videoUrl);
    const handleClick = () => setIsVideoVisible(true);

    useEffect(() => {
        if (isVideoVisible && videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play().catch((err) => {
                console.warn("Video play failed", err);
            });
        }
    }, [isVideoVisible]);

    if (mediaType === "video") {
        return (
            <>
                {preview && (
                    <ContentfulInspectorManager
                        fields={[
                            {
                                entryId: data?.mediaCollection?.items?.[0]?.sys?.id,
                                fieldId: "url",
                                targetSelector: "#vedioUrlthumnailvedio",
                            },
                            {
                                entryId: data?.mediaCollection?.items?.[0]?.sys?.id,
                                fieldId: "url",
                                targetSelector: "#thumnailimageforvedio",
                            },

                        ]}
                    />
                )}
                <div key={key} className="video-container flex justify-center items-center rounded-[8px] overflow-hidden relative">
                    {isVideoVisible ? (
                        <video
                            id="vedioUrlthumnailvedio"
                            ref={videoRef}
                            className="w-full"
                            src={videoUrl}
                            controls
                            controlsList="nodownload"
                            data-testid="video-element"
                        />
                    ) : (
                        <div className="w-full relative rounded-[8px] overflow-hidden block">
                            {Boolean(thumbnailUrl) && (
                                <Image
                                    id="thumnailimageforvedio"
                                    className="block w-full"
                                    src={thumbnailUrl}
                                    alt="Video thumbnail"
                                    width={600}
                                    height={316}
                                    sizes="(max-width: 640px) 100vw,
                (max-width: 1024px) 90vw,
                80vw"
                                    quality={75}
                                />
                            )}

                            <button
                                type="button"
                                onClick={handleClick}
                                className="video-play-icon absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                                aria-label="Play Video"
                            >
                                <Image
                                    src="/static/assets/icons/video_play_icon.svg"
                                    width={52}
                                    height={52}
                                    alt="Play icon"
                                />
                            </button>
                        </div>
                    )}
                </div>
            </>
        );
    }

    if (mediaType === "file") {
        const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` :videoUrl;
        if (videoId && !playVideo) {
            const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            return (
                <>
                    {preview && (
                        <ContentfulInspectorManager
                            fields={[
                                {
                                    entryId: data?.mediaCollection?.items?.[0]?.sys?.id || "",
                                    fieldId: "externalVideoUrl",
                                    targetSelector: "#youtubeThumbnail",
                                },
                            ]}
                        />
                    )}
                    <div
                        id="youtubeThumbnail"
                        style={{ position: "relative", cursor: "pointer" }}
                        onClick={() => setPlayVideo(true)}
                    >
                        <Image
                            src={thumbnailUrl}
                            alt="YouTube Video Thumbnail"
                            width={800}
                            height={500}
                            style={{ width: "100%", height: "auto" }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: "48px",
                                color: "white",
                                background: "rgba(0,0,0,0.5)",
                                borderRadius: "50%",
                                padding: "10px",
                            }}
                        >
                            <Image
                                    src="/static/assets/icons/video_play_icon.svg"
                                    width={52}
                                    height={52}
                                    alt="Play icon"
                                />
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                {preview && (
                    <ContentfulInspectorManager
                        fields={[
                            {
                                entryId: data?.mediaCollection?.items?.[0]?.sys?.id || "",
                                fieldId: "url",
                                targetSelector: "#embeddedvedio",
                            },
                        ]}
                    />
                )}
                <iframe
                    id="embeddedvedio"
                    src={embedUrl}
                    style={{ width: "100%", height: "500px", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </>
        );
    }

    return <>Unsupported media type</>;
}
